import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import Slider from "react-slick";
import { useDispatch } from "react-redux";
import { handleListWhatsNew } from "store/whatsNew/thunks";
import { useWhatsNew } from "store/whatsNew/selectors";
import { useCurrentUser } from "store/users/selectors";
import SentryWrap from 'components/Sentry';

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #0a0a0a;
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
  }
`;

export default function TeacherWhatsNew() {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const whatsNewData = useWhatsNew();
  const [whatsNew, setWhatsNew] = useState([])
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    dispatch(handleListWhatsNew());
  }, []);

  useEffect(() => {
    if (whatsNewData) {
      const filterByRole = whatsNewData?.filter((k) => k?.user_role.includes(currentUser?.role));
      setWhatsNew(filterByRole);
    }
  }, [whatsNewData])

  useEffect(() => {
    if (whatsNew?.length) {
      setSelectedId(whatsNew[0].id);
    }
  }, [whatsNew]);

  const selectedItem = whatsNew?.find((item) => item.id === selectedId);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    swipe: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  // const [isModalOpen, setIsModalOpen] = useState(false);

  // const handleOpenModal = () => {
  //   setIsModalOpen(true);
  // };

  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  // };

  return (
    <SentryWrap>
      <GlobalStyle />
      <Wrapper>
        <Header>
          <Title>🚀 What’s New</Title>
          {whatsNew?.length > 1 && (
            <Dropdown
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {whatsNew?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.new_version} -{" "}
                  {new Date(item.create_date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </option>
              ))}
            </Dropdown>
          )}
        </Header>
        <ContentSection>
          {selectedItem ? (
            <div key={selectedItem.id}>
              <DateWrapper>
                <DateLine />
                <StyledDate>
                  {new Date(selectedItem.create_date).toLocaleDateString(
                    "en-GB",
                    { day: "numeric", month: "short", year: "numeric" }
                  )}
                </StyledDate>
                <DateLine />
              </DateWrapper>

              <Intro>
                <Highlight>✨ Introducing</Highlight>{" "}
                <span style={{ color: "white" }}>{selectedItem.title}</span>
              </Intro>
              <SubTitle>{selectedItem.subtitle}</SubTitle>

              <MediaContainer>
                <StyledVideo
                  autoPlay
                  loop
                  muted
                  controls
                  src={selectedItem.video_url}
                />
              </MediaContainer>

              <Details>
                <Description
                  className="techrDetailSection"
                  dangerouslySetInnerHTML={{
                    __html: selectedItem.long_description,
                  }}
                />
              </Details>

              {selectedItem.image_urls.length > 1 ? (
                <ImageSlider {...sliderSettings}>
                  {selectedItem.image_urls.map((image, index) => (
                    <StyledImage
                      key={index}
                      src={image}
                      alt={`Slide ${index + 1}`}
                    />
                  ))}
                </ImageSlider>
              ) : (
                <SingleImage src={selectedItem.image_urls[0]} alt="Preview" />
              )}

              <Details>
                <Description
                  className="techrDetailSection"
                  dangerouslySetInnerHTML={{
                    __html: selectedItem.short_description,
                  }}
                />
              </Details>
            </div>
          ) : (
            <p>No "What's New" content available.</p>
          )}
        </ContentSection>
      </Wrapper>
    </SentryWrap>
  );
}

// Styled Components
const Wrapper = styled.div`
  color: #fff;
  background-color: #0a0a0a;
  margin: 0 auto;
  text-align: center;
  padding-top: 0px;
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  background-color: #0a0e16;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 1rem;
  border-bottom: 1px solid #222;
`;

const ContentSection = styled.div`
  padding: 2rem 1rem;
  padding-top: 0px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0.5rem 0;
  color: white;
`;

const Dropdown = styled.select`
  background: #1a1a1a;
  color: #fff;
  border: 1px solid #333;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  margin: 0.5rem 0;
`;

const StyledDate = styled.div`
  font-size: 0.9rem;
  color: #bbb;
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

const DateLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: #222;
  margin: 0 10px;
`;

const Intro = styled.h2`
  font-size: 1.4rem;
  margin: 1rem 0 0.5rem;
`;

const Highlight = styled.span`
  background: linear-gradient(
    90deg,
    #cb5b9c 44.92%,
    #a852e5 69.32%,
    #7466d1 99.98%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  color: #aaa;
  font-size: 1rem;
`;

const MediaContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
`;

const StyledVideo = styled.video`
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
`;

const StyledImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
`;

const SingleImage = styled.img`
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
  margin: 2rem 0;
  object-fit: cover;
`;

const ImageSlider = styled(Slider)`
  max-width: 800px;
  margin: 2rem auto;

  .slick-slide > div {
    padding: 0 10px; /* Controls space between images */
  }

  .slick-list {
    margin: 0 -10px; /* Compensates for padding on the edges */
  }

  .slick-slide img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }

  .slick-dots li button:before {
    color: #fff;
  }

  .slick-prev:before,
  .slick-next:before {
    color: #fff;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  margin-top: 1rem;
  color: #ccc;
  max-width: 800px;
  margin: 0 auto;
`;

const Details = styled.ul`
  list-style: disc;
  text-align: left;
  padding: 0 1rem;
  margin: 1.5rem auto;
  max-width: 800px;
  color: #ccc!important;

  li {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const Like = styled.p`
  color: #ccc;
  font-size: 0.9rem;
`;

